(function () {
  const container = document.querySelector('[data-merken-az]');
  if (!container) return;

  const shopDomain = container.dataset.shopDomain;
  const token = container.dataset.storefrontToken;
  const collectionHandle = container.dataset.collectionHandle;
  const collectionId = container.dataset.collectionId;
  const language = (container.dataset.language || '').toUpperCase();
  const normalizeMarket = (value) => ({ belgie: 'be', netherlands: 'nl' }[String(value).trim().toLowerCase()] || String(value).trim().toLowerCase());
  const market = normalizeMarket(container.dataset.marketHandle || '');
  const isGerman = language === 'DE' || language.startsWith('DE-');
  // Locale root ("/" on primary, "/fr" on the French market). onlineStoreUrl
  // from the Storefront API points at the default locale, so build collection
  // links from the localised handle + this prefix to keep visitors in /fr.
  const rootUrl = (container.dataset.rootUrl || '/').replace(/\/$/, '');
  const metafieldNamespace = container.dataset.metafieldNamespace;
  const metafieldKey = container.dataset.metafieldKey;

  if (!token) {
    container.innerHTML = isGerman
      ? '<p class="merken-overview__az-error">Das Storefront-API-Token fehlt. Füge es über die Theme-Einstellungen &rsaquo; API-Einstellungen hinzu.</p>'
      : '<p class="merken-overview__az-error">Storefront API token ontbreekt. Voeg deze toe via Theme instellingen &rsaquo; API instellingen.</p>';
    return;
  }

  // Look up the collection by GID (stable across locales) rather than handle —
  // translated markets serve a translated handle (e.g. "merken" -> "marques")
  // that the default-language Storefront API does not resolve, which returned
  // null references and an empty brand list. @inContext localises titles/URLs.
  const collectionGid = collectionId ? 'gid://shopify/Collection/' + collectionId : null;
  const contextDirective = language ? '@inContext(language: ' + language + ')' : '';

  const METAFIELD = `
    metafield(namespace: "${metafieldNamespace}", key: "${metafieldKey}") {
      references(first: 50, after: $cursor) {
        pageInfo { hasNextPage endCursor }
        nodes {
          ... on Collection {
            title
            handle
            activeMarkets: metafield(namespace: "custom", key: "active_for_markets") { value }
            onlineStoreUrl
            image { url(transform: { maxWidth: 240 }) altText }
          }
        }
      }
    }
  `;

  // Look the collection up by GID via node() — stable across locales. Translated
  // markets serve a translated handle (e.g. "merken" -> "marques") that the
  // default-language Storefront API cannot resolve, so a handle lookup returned
  // null references and an empty brand list. Fall back to handle when no id.
  const QUERY = collectionGid
    ? `
      query GetBrandCollections($id: ID!, $cursor: String) ${contextDirective} {
        node(id: $id) {
          ... on Collection { ${METAFIELD} }
        }
      }
    `
    : `
      query GetBrandCollections($handle: String!, $cursor: String) ${contextDirective} {
        collection(handle: $handle) { ${METAFIELD} }
      }
    `;

  async function fetchPage(cursor) {
    const response = await fetch(`https://${shopDomain}/api/2024-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: QUERY,
        variables: collectionGid
          ? { id: collectionGid, cursor: cursor || null }
          : { handle: collectionHandle, cursor: cursor || null },
      }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  }

  async function fetchAllBrands() {
    const brands = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await fetchPage(cursor);
      if (data?.errors?.length) {
        throw new Error('GraphQL: ' + data.errors.map(function (e) { return e.message; }).join('; '));
      }
      const root = data?.data?.node || data?.data?.collection;
      const refs = root?.metafield?.references;
      if (!refs) break;
      refs.nodes.forEach(function (node) {
        const selection = node.activeMarkets?.value ? JSON.parse(node.activeMarkets.value) : [];
        if (!selection.length || selection.some((value) => normalizeMarket(value) === market)) {
          brands.push(node);
        }
      });
      hasNextPage = refs.pageInfo.hasNextPage;
      cursor = refs.pageInfo.endCursor;
    }

    return brands;
  }

  function buildAlphabetNav(letterMap) {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const nav = document.createElement('nav');
    nav.className = 'merken-overview__alphabet';
    nav.setAttribute('aria-label', 'Merken op letter');

    letters.forEach(function (letter) {
      if (letterMap[letter]) {
        const a = document.createElement('a');
        a.className = 'merken-overview__alphabet-link';
        a.href = '#letter-' + letter.toLowerCase();
        a.textContent = letter;
        nav.appendChild(a);
      } else {
        const span = document.createElement('span');
        span.className = 'merken-overview__alphabet-link merken-overview__alphabet-link--disabled';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = letter;
        nav.appendChild(span);
      }
    });

    return nav;
  }

  function buildGroups(letterMap) {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    const groups = document.createElement('div');
    groups.className = 'merken-overview__groups';

    letters.forEach(function (letter) {
      if (!letterMap[letter]) return;

      const section = document.createElement('section');
      section.className = 'merken-overview__group';
      section.id = 'letter-' + letter.toLowerCase();

      const h2 = document.createElement('h2');
      h2.className = 'merken-overview__letter';
      h2.textContent = letter;
      section.appendChild(h2);

      const ul = document.createElement('ul');
      ul.className = 'merken-overview__items';
      ul.setAttribute('role', 'list');

      letterMap[letter].forEach(function (brand) {
        const li = document.createElement('li');
        li.className = 'merken-overview__item';

        const a = document.createElement('a');
        a.className = 'merken-overview__link';
        a.href = rootUrl + '/collections/' + brand.handle;

        const logoWrap = document.createElement('span');
        logoWrap.className = 'merken-overview__logo-wrap';

        if (brand.image) {
          const img = document.createElement('img');
          img.className = 'merken-overview__logo';
          img.src = brand.image.url;
          img.alt = brand.image.altText || brand.title;
          img.loading = 'lazy';
          img.width = 120;
          img.height = 60;
          logoWrap.appendChild(img);
        } else {
          const fallback = document.createElement('span');
          fallback.className = 'merken-overview__logo-fallback';
          fallback.textContent = brand.title.slice(0, 2).toUpperCase();
          logoWrap.appendChild(fallback);
        }

        const name = document.createElement('span');
        name.className = 'merken-overview__name';
        name.textContent = brand.title;

        a.appendChild(logoWrap);
        a.appendChild(name);
        li.appendChild(a);
        ul.appendChild(li);
      });

      section.appendChild(ul);
      groups.appendChild(section);
    });

    return groups;
  }

  fetchAllBrands()
    .then(function (brands) {
      brands.sort(function (a, b) {
        return a.title.localeCompare(b.title, 'nl', { sensitivity: 'base' });
      });

      const letterMap = {};
      brands.forEach(function (brand) {
        const letter = brand.title.trim().charAt(0).toUpperCase();
        if (!letterMap[letter]) letterMap[letter] = [];
        letterMap[letter].push(brand);
      });

      const fragment = document.createDocumentFragment();
      fragment.appendChild(buildAlphabetNav(letterMap));
      fragment.appendChild(buildGroups(letterMap));

      container.innerHTML = '';
      container.appendChild(fragment);
    })
    .catch(function (err) {
      console.error('[fw-merken-overview]', err);
      container.innerHTML = isGerman
        ? '<p class="merken-overview__az-error">Die Marken konnten nicht geladen werden. Bitte lade die Seite neu.</p>'
        : '<p class="merken-overview__az-error">Merken konden niet worden geladen. Probeer de pagina te vernieuwen.</p>';
    });
})();
